import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatErrorMessage } from '@/lib/error-utils';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { adminIntegrations, isSharedBackendAvailable, getToken } from '@/api/client';
import {
  Key, CreditCard, MessageSquare, Mail, Shield, MapPin, FileCheck,
  Settings, RefreshCw, CheckCircle, XCircle, AlertCircle, History
} from 'lucide-react';

const providerIcons = {
  stripe: CreditCard,
  twilio: MessageSquare,
  brevo: Mail,
  checkr: Shield,
  google_maps: MapPin,
  sendgrid: Mail,
  plaid: FileCheck,
};

const providerNames = {
  stripe: 'Stripe',
  twilio: 'Twilio',
  brevo: 'Brevo',
  checkr: 'Checkr',
  google_maps: 'Google Maps',
  sendgrid: 'SendGrid',
  plaid: 'Plaid',
};

// Define required API keys and configuration for each provider based on their official documentation
const providerSchemas = {
  stripe: {
    secrets: {
      secret_key: { label: 'Secret Key', placeholder: 'sk_test_... or sk_live_...', required: true, help: 'Used for server-side API calls' },
      webhook_secret: { label: 'Webhook Secret', placeholder: 'whsec_...', required: false, help: 'For verifying webhook signatures' }
    },
    config: {},
    docs: 'https://stripe.com/docs/keys',
    disclaimer: '⚠️ Test mode keys start with sk_test_. Live mode keys start with sk_live_. Never share your secret keys.'
  },
  twilio: {
    secrets: {
      account_sid: { label: 'Account SID', placeholder: 'AC...', required: true, help: 'Your Twilio Account SID' },
      auth_token: { label: 'Auth Token', placeholder: 'Your auth token', required: true, help: 'Your Twilio Auth Token' }
    },
    config: {
      from_number: { label: 'From Phone Number', placeholder: '+1234567890', required: true, help: 'Twilio phone number (E.164 format with country code)' }
    },
    docs: 'https://www.twilio.com/docs/iam/keys/api-key',
    disclaimer: 'Find credentials in Twilio Console > Account > API Credentials. Phone number: +1-704-396-5144'
  },
  brevo: {
    secrets: {
      api_key: { label: 'API Key', placeholder: 'xkeysib-...', required: true, help: 'Your Brevo API key' }
    },
    config: {
      sender_email: { label: 'Sender Email', placeholder: 'noreply@r2yn.com', required: true, help: 'Email address to send from' },
      sender_name: { label: 'Sender Name', placeholder: 'R2YN Alerts', required: false, help: 'Display name for sender' }
    },
    docs: 'https://developers.brevo.com/docs/getting-started',
    disclaimer: 'API key can be found in Settings > SMTP & API > API Keys. SMTP: smtp-relay.brevo.com:587'
  },
  checkr: {
    secrets: {
      api_key: { label: 'API Key', placeholder: 'Your Checkr API key', required: true, help: 'For background check requests' }
    },
    config: {},
    docs: 'https://docs.checkr.com/#section/Authentication',
    disclaimer: 'Test API keys work with sandbox accounts. Production requires compliance review.'
  },
  google_maps: {
    secrets: {
      api_key: { label: 'API Key', placeholder: 'AIza...', required: true, help: 'Your Google Maps API key' }
    },
    config: {},
    docs: 'https://developers.google.com/maps/documentation/javascript/get-api-key',
    disclaimer: '⚠️ Enable required APIs: Maps JavaScript API, Places API, Geocoding API. Restrict key usage by HTTP referrer or IP.'
  }
};

export default function AdminIntegrations() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedMode, setSelectedMode] = useState('live');
  const [editDialog, setEditDialog] = useState(false);
  const [testDialog, setTestDialog] = useState(false);
  const [auditDialog, setAuditDialog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state for editing
  const [formData, setFormData] = useState({
    secrets: {},
    config: {}
  });

  useEffect(() => {
    // Wrap in try-catch to prevent unhandled promise rejections
    const loadIntegrations = async () => {
      try {
        await fetchIntegrations();
      } catch (err) {
        // Check if it's an auth error and we're missing the shared token
        if (err?.message?.includes('Unauthorized') || err?.message?.includes('401')) {
          const sharedToken = getToken(); // Check for shared backend token
          if (!sharedToken) {
            setError('Session expired or missing shared backend authentication. Please log out and log back in to access the integrations vault.');
            setLoading(false);
            return;
          }
        }
        // Errors are handled in fetchIntegrations, this is just a safety net
        console.warn('[useEffect] Error suppressed:', err?.message);
      }
    };
    
    loadIntegrations();
  }, []);

  const handleRetry = async () => {
    try {
      await fetchIntegrations();
    } catch (err) {
      // Silently catch any errors that escaped fetchIntegrations
      console.warn('[Retry] Error suppressed:', err?.message);
    }
  };

  const fetchIntegrations = async () => {
    setLoading(true);
    setError(null);
    
    // Check if shared backend is configured
    if (!isSharedBackendAvailable()) {
      setError('Shared backend is not configured. The integrations vault requires connection to the R2YN mobile backend.');
      setLoading(false);
      return;
    }
    
    try {
      // Wrap fetch in additional promise to ensure errors don't bubble
      const data = await adminIntegrations.list().catch(fetchError => {
        throw fetchError; // Re-throw to be caught by outer try-catch
      });
      
      // Transform flat list to nested structure grouped by provider
      // Backend returns: [{ provider: "stripe", mode: "test", ... }, { provider: "stripe", mode: "live", ... }]
      // Frontend expects: [{ provider: "stripe", test_mode: {...}, live_mode: {...} }]
      const rawIntegrations = data.integrations || [];
      const groupedIntegrations = {};
      
      rawIntegrations.forEach(integration => {
        const provider = integration.provider;
        const mode = integration.mode; // "test" or "live"
        
        if (!groupedIntegrations[provider]) {
          groupedIntegrations[provider] = {
            provider: provider,
            label: integration.label,
            description: integration.description,
            supports_test_live: integration.supports_test_live,
            test_mode: null,
            live_mode: null
          };
        }
        
        // Create mode object with the structure expected by the UI
        const modeData = {
          active: integration.active,
          has_keys: integration.has_keys,
          masked_keys: integration.masked_keys || {},
          config: integration.config || {},
          updated_at: integration.updated_at,
          updated_by: integration.updated_by
        };
        
        if (mode === 'test') {
          groupedIntegrations[provider].test_mode = modeData;
        } else if (mode === 'live') {
          groupedIntegrations[provider].live_mode = modeData;
        }
      });
      
      // Convert grouped object back to array
      const transformedIntegrations = Object.values(groupedIntegrations);
      
      setIntegrations(transformedIntegrations);
    } catch (err) {
      // Use the formatErrorMessage utility for consistent error handling
      setError(formatErrorMessage(err, 'Load integrations'));
      toast.error(formatErrorMessage(err, 'Load integrations'));
      
      // Prevent error from propagating to React error boundary
      return; // Explicitly return to end function
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const data = await adminIntegrations.audit(50);
      setAuditLogs(data.entries || []);
      setAuditDialog(true);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
      toast.error('Failed to load audit log');
    }
  };

  const handleEdit = async (provider, mode) => {
    try {
      // Get schema for this provider
      const schema = providerSchemas[provider] || { secrets: {}, config: {} };
      
      // Try to fetch existing config from backend
      let existingSecrets = {};
      let existingConfig = {};
      
      try {
        const data = await adminIntegrations.get(provider, mode);
        existingSecrets = data.secrets || {};
        existingConfig = data.config || {};
      } catch (err) {
        console.warn('Could not fetch existing config, using schema defaults:', err.message);
      }
      
      // Merge schema with existing values to ensure all fields are present
      const secretsForm = {};
      Object.keys(schema.secrets).forEach(key => {
        secretsForm[key] = existingSecrets[key] || '';
      });
      
      const configForm = {};
      Object.keys(schema.config).forEach(key => {
        configForm[key] = existingConfig[key] || '';
      });
      
      setFormData({
        secrets: secretsForm,
        config: configForm
      });
      
      setSelectedProvider(provider);
      setSelectedMode(mode);
      setEditDialog(true);
    } catch (err) {
      console.error('Failed to open configuration:', err);
      toast.error('Failed to load configuration dialog');
    }
  };

  const handleSave = async () => {
    if (!selectedProvider) return;

    setSaving(true);
    try {
      // Only send fields that have been modified (non-empty)
      const secret_updates = Object.entries(formData.secrets)
        .filter(([_, value]) => value && value.trim())
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      const config_updates = Object.entries(formData.config)
        .filter(([_, value]) => value && value.trim())
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      await adminIntegrations.update(
        selectedProvider,
        selectedMode,
        secret_updates,
        config_updates
      );

      toast.success(`${providerNames[selectedProvider]} updated successfully`);
      setEditDialog(false);
      fetchIntegrations();
    } catch (err) {
      console.error('Failed to update integration:', err);
      toast.error(err.message || 'Failed to update integration');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (provider, mode, currentActive) => {
    try {
      await adminIntegrations.toggle(provider, mode, !currentActive);
      toast.success(`${providerNames[provider]} ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchIntegrations();
    } catch (err) {
      console.error('Failed to toggle integration:', err);
      toast.error('Failed to toggle integration');
    }
  };

  const handleTest = async (provider, mode) => {
    setTesting(true);
    setTestDialog(true);
    
    try {
      const result = await adminIntegrations.test(provider, mode);
      
      if (result.ok) {
        toast.success(`${providerNames[provider]} connection successful!`);
      } else {
        toast.error(`${providerNames[provider]} test failed: ${result.detail}`);
      }
    } catch (err) {
      console.error('Test failed:', err);
      toast.error(err.message || 'Connection test failed');
    } finally {
      setTesting(false);
      setTimeout(() => setTestDialog(false), 2000);
    }
  };

  const getProviderStatus = (provider) => {
    const testActive = provider.test_mode?.active;
    const liveActive = provider.live_mode?.active;
    
    if (liveActive) return { status: 'live', label: 'Live', color: 'bg-green-500' };
    if (testActive) return { status: 'test', label: 'Test', color: 'bg-yellow-500' };
    return { status: 'inactive', label: 'Inactive', color: 'bg-gray-400' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="h-8 w-8 animate-spin text-[#9A031E]" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Integrations Vault</h1>
          <p className="text-muted-foreground">
            Manage API keys and configurations for third-party services
          </p>
        </div>
        <Button onClick={fetchAuditLog} variant="outline" disabled={!!error}>
          <History className="mr-2 h-4 w-4" />
          View Audit Log
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-destructive mb-2">Connection Error</h3>
                <p className="text-sm text-muted-foreground mb-4">{error}</p>
                <div className="flex gap-3">
                  {error.includes('log out and log back in') ? (
                    <Button 
                      onClick={() => {
                        // Force logout and redirect to login
                        window.localStorage.clear();
                        window.location.href = '/login';
                      }} 
                      size="sm"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Log Out & Re-Login
                    </Button>
                  ) : (
                    <Button onClick={handleRetry} size="sm" variant="outline">
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Retry
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!error && integrations.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Key className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No Integrations Found</h3>
              <p className="text-sm text-muted-foreground">
                No third-party integrations are currently configured.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Integration Cards */}
      {!error && integrations.length > 0 && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations.map((integration) => {
          const Icon = providerIcons[integration.provider] || Key;
          const status = getProviderStatus(integration);
          
          return (
            <Card key={integration.provider} className="relative overflow-hidden">
              {/* Status Badge */}
              <div className={`absolute top-0 right-0 w-2 h-full ${status.color}`} />
              
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#9A031E]/10">
                      <Icon className="h-6 w-6 text-[#9A031E]" />
                    </div>
                    <div>
                      <CardTitle>{providerNames[integration.provider]}</CardTitle>
                      <Badge variant="outline" className="mt-1">
                        {status.label}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <Tabs defaultValue="live" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="live">Live</TabsTrigger>
                    <TabsTrigger value="test">Test</TabsTrigger>
                  </TabsList>

                  <TabsContent value="live" className="space-y-3">
                    {integration.live_mode && (
                      <>
                        {/* Masked Keys */}
                        {integration.live_mode.masked_keys && (
                          <div className="space-y-2">
                            {Object.entries(integration.live_mode.masked_keys).map(([key, value]) => (
                              <div key={key} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{key}:</span>
                                <span className="font-mono">{value}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(integration.provider, 'live')}
                            className="flex-1"
                          >
                            <Settings className="mr-1 h-3 w-3" />
                            Configure
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTest(integration.provider, 'live')}
                            disabled={!integration.live_mode.active}
                          >
                            <CheckCircle className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Toggle */}
                        <div className="flex items-center justify-between pt-2">
                          <Label htmlFor={`live-${integration.provider}`}>Active</Label>
                          <Switch
                            id={`live-${integration.provider}`}
                            checked={integration.live_mode.active}
                            onCheckedChange={() =>
                              handleToggle(integration.provider, 'live', integration.live_mode.active)
                            }
                          />
                        </div>
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="test" className="space-y-3">
                    {integration.test_mode && (
                      <>
                        {/* Masked Keys */}
                        {integration.test_mode.masked_keys && (
                          <div className="space-y-2">
                            {Object.entries(integration.test_mode.masked_keys).map(([key, value]) => (
                              <div key={key} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{key}:</span>
                                <span className="font-mono">{value}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(integration.provider, 'test')}
                            className="flex-1"
                          >
                            <Settings className="mr-1 h-3 w-3" />
                            Configure
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTest(integration.provider, 'test')}
                            disabled={!integration.test_mode.active}
                          >
                            <CheckCircle className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Toggle */}
                        <div className="flex items-center justify-between pt-2">
                          <Label htmlFor={`test-${integration.provider}`}>Active</Label>
                          <Switch
                            id={`test-${integration.provider}`}
                            checked={integration.test_mode.active}
                            onCheckedChange={() =>
                              handleToggle(integration.provider, 'test', integration.test_mode.active)
                            }
                          />
                        </div>
                      </>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          );
        })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Configure {selectedProvider && providerNames[selectedProvider]} - {selectedMode === 'live' ? 'Live' : 'Test'} Mode
            </DialogTitle>
            <DialogDescription>
              Enter your API keys and configuration. Leave fields empty to keep existing values.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Disclaimer */}
            {selectedProvider && providerSchemas[selectedProvider]?.disclaimer && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  {providerSchemas[selectedProvider].disclaimer}
                </p>
              </div>
            )}

            {/* Docs Link */}
            {selectedProvider && providerSchemas[selectedProvider]?.docs && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <FileCheck className="h-4 w-4" />
                <a 
                  href={providerSchemas[selectedProvider].docs} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:underline text-blue-600 dark:text-blue-400"
                >
                  View Official Documentation →
                </a>
              </div>
            )}

            {/* Secrets Section */}
            <div>
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Key className="h-4 w-4" />
                API Keys (Encrypted)
              </h3>
              <div className="space-y-3">
                {selectedProvider && providerSchemas[selectedProvider]?.secrets && 
                  Object.entries(providerSchemas[selectedProvider].secrets).map(([key, schema]) => (
                  <div key={key}>
                    <Label htmlFor={`secret-${key}`} className="flex items-center gap-2">
                      {schema.label}
                      {schema.required && <span className="text-destructive">*</span>}
                    </Label>
                    {schema.help && (
                      <p className="text-xs text-muted-foreground mt-1 mb-2">{schema.help}</p>
                    )}
                    <Input
                      id={`secret-${key}`}
                      type="password"
                      placeholder={schema.placeholder}
                      value={formData.secrets?.[key] || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          secrets: { ...formData.secrets, [key]: e.target.value }
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center gap-2">
            <Button 
              variant="outline" 
              onClick={async () => {
                if (!selectedProvider) return;
                setTesting(true);
                try {
                  await handleTest(selectedProvider, selectedMode);
                  toast.success('Connection test passed!');
                } catch (err) {
                  toast.error('Connection test failed');
                } finally {
                  setTesting(false);
                }
              }}
              disabled={testing || saving}
            >
              {testing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Test Connection
                </>
              )}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || testing}>
                {saving ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={testDialog} onOpenChange={setTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Testing Connection</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8">
            {testing ? (
              <>
                <RefreshCw className="h-12 w-12 animate-spin text-[#9A031E] mb-4" />
                <p className="text-sm text-muted-foreground">Verifying connection...</p>
              </>
            ) : (
              <>
                <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
                <p className="font-medium">Connection Successful!</p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditDialog} onOpenChange={setAuditDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Integration Audit Log</DialogTitle>
            <DialogDescription>
              Track all changes made to integration configurations
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-3">
              {auditLogs.map((log, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{log.provider}</Badge>
                      <Badge>{log.action}</Badge>
                      {log.mode && <Badge variant="secondary">{log.mode}</Badge>}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <p><strong>Actor:</strong> {log.actor_email}</p>
                    {log.changes && (
                      <p className="text-muted-foreground">
                        <strong>Changes:</strong> {JSON.stringify(log.changes)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
